import { normalizeCategoryId } from './categories';

const MIN_AUTO_VEHICLE_PURCHASE_AMOUNT = 1000;

const VEHICLE_PURCHASE_PATTERN = /\b(vehicle|car|auto|motor|motorbike|motorcycle|scooter|van|truck|dealer|dealership|showroom|registration|number plate|license plate|vin|trade[-\s]?in|odometer|chassis)\b/i;
const VEHICLE_FUEL_PATTERN = /\b(vehicle fuel|car fuel|fuel|diesel|petrol|gasoline|unleaded|pump|circle\s*k|applegreen|topaz|maxol)\b/i;
const VEHICLE_TRAVEL_PATTERN = /\b(parking|toll|taxi|uber|lyft|train|bus|ticket|fare|station|platform|car rental)\b/i;
const VEHICLE_REPAIR_PATTERN = /\b(garage|mechanic|tyre|tire|oil change|brake|windscreen|wheel alignment|car wash|service center|service centre|car service|vehicle service|auto service|car repair|vehicle repair|auto repair|motor repair|motor tax|road tax|car tax|vehicle tax|vehicle licensing|vehicle licence|vehicle license|licensing renewal|licence renewal|license renewal|nct|mot)\b/i;
const INSURANCE_PATTERN = /\b(insurance|policy|premium|cover)\b/i;
const GROCERY_VENDOR_PATTERN = /\b(supervalu|tesco|dunnes|lidl|aldi|spar|centra|iceland|marks?\s*&\s*spencer|m&s)\b/i;
const MEAL_VENDOR_PATTERN = /\b(coffee|cafe|starbucks|costa|nero|restaurant|bakery|deli|subway|mcdonald|burger king|domino|pizza)\b/i;
const MEAL_PATTERN = /\b(coffee|tea|sandwich|croissant|pastry|cake|snack|water|juice|roll|bun|breakfast|lunch|dinner|meal|salad|yogurt|pizza|burger)\b/i;
const MEDICAL_PATTERN = /\b(pharmacy|chemist|prescriptions?|scripts?|medical|doctor|clinic|hospital|healthcare|dental|dentist)\b/i;
const HARDWARE_VENDOR_PATTERN = /\b(brooks|chadwicks|woodies|hardware|diy|builders?\s*merchant|building\s*supplies|screwfix|b\s*&\s*q)\b/i;
const AUTO_PARTS_PATTERN = /\b(otto\s*bray|car\s*parts|motor\s*factors?|vehicle\s*parts|auto\s*parts|coolant|headlamp|bulb|wiper|brake\s*pads?|spark\s*plug|oil\s*filter)\b/i;
const CLOTHING_VENDOR_PATTERN = /\b(penneys|primark|zara|h&m|new look|next|river island|mango)\b/i;
const CLOTHING_PATTERN = /\b(tee|shirt|top|dress|trouser|jean|short|pant|skirt|coat|jacket|hoodie|sweater|cardigan|blouse|sock|underwear|bra|brief|legging|jogger|vest|polo|tank|cami|blazer|suit|tie|scarf|hat|cap|glove|boot|shoe|sandal|trainer|heel|bag|purse|wallet|belt|watch|sunglass)\b/i;
const BEAUTY_PATTERN = /\b(shampoo|conditioner|lotion|cream|makeup|mascara|lipstick|nail|polish|hair|soap|facial|biab|builder gel|gel bottle|gelbottle|brow tint|eyebrow tint|lash tint|tinting product|brow dye|eyebrow dye|face mask|hair mask|mask|hot wax|strip wax|soft wax|hard wax|wax)\b/i;
const GENERIC_RECEIPT_CATEGORY_IDS = new Set([
  '',
  'supplies',
  'misc',
  'other',
  'general_expenses',
  'utilities',
  'travel',
  'travel_expenses',
  'repairs'
]);

const normalizeReceiptLikeCategory = (category, description = '') => {
  const normalized = normalizeCategoryId(category || '');
  if (normalized === 'clothing') return 'uniform_costs';
  if (normalized === 'beauty') return 'supplies';
  if (normalized === 'healthcare' || normalized === 'medical') return 'medical_expenses';
  if (normalized === 'travel') return 'travel_expenses';
  if (normalized === 'travel_expenses' && VEHICLE_FUEL_PATTERN.test(description)) return 'vehicle_fuel';
  if (normalized === 'repairs' && VEHICLE_REPAIR_PATTERN.test(description)) return 'vehicle_repairs_licensing';
  if (normalized && normalized !== 'vehicle' && normalized !== 'misc' && normalized !== 'other') {
    return normalized;
  }

  const descriptionText = String(description || '');
  if (MEDICAL_PATTERN.test(descriptionText)) return 'medical_expenses';
  if (CLOTHING_PATTERN.test(descriptionText)) return 'uniform_costs';
  if (MEAL_PATTERN.test(descriptionText)) return 'meals';
  if (BEAUTY_PATTERN.test(descriptionText)) return 'supplies';
  if (VEHICLE_FUEL_PATTERN.test(descriptionText)) return 'vehicle_fuel';
  if (VEHICLE_TRAVEL_PATTERN.test(descriptionText)) return 'travel_expenses';
  if (VEHICLE_REPAIR_PATTERN.test(descriptionText)) return 'vehicle_repairs_licensing';
  if (INSURANCE_PATTERN.test(descriptionText)) return 'insurance';
  return '';
};

const getDominantNonVehicleItemCategory = (items = []) => {
  const counts = new Map();

  items.forEach((item) => {
    const description = String(item?.description || item?.name || item?.item || '').trim();
    const category = normalizeReceiptLikeCategory(item?.category || '', description);
    if (!category || category === 'vehicle') return;
    counts.set(category, (counts.get(category) || 0) + 1);
  });

  let bestCategory = '';
  let bestCount = 0;
  counts.forEach((count, category) => {
    if (count > bestCount) {
      bestCategory = category;
      bestCount = count;
    }
  });

  return bestCategory;
};

export const guardAutoDetectedReceiptExpenseCategory = ({
  category,
  amount,
  vendor = '',
  items = [],
  rawText = ''
} = {}) => {
  const normalizedCategory = normalizeCategoryId(category || '');
  const vendorText = String(vendor || '');
  const combinedText = [
    vendorText,
    rawText,
    ...(Array.isArray(items) ? items.map((item) => `${item?.description || ''} ${item?.category || ''}`) : [])
  ].join(' ');

  if (normalizedCategory !== 'vehicle' && !GENERIC_RECEIPT_CATEGORY_IDS.has(normalizedCategory)) {
    return normalizedCategory || '';
  }

  const dominantItemCategory = getDominantNonVehicleItemCategory(items);
  if (dominantItemCategory && !GENERIC_RECEIPT_CATEGORY_IDS.has(dominantItemCategory)) {
    return dominantItemCategory;
  }

  const absoluteAmount = Math.abs(Number(amount) || 0);

  if (INSURANCE_PATTERN.test(combinedText)) return 'insurance';
  if (MEDICAL_PATTERN.test(combinedText)) return 'medical_expenses';
  if (VEHICLE_FUEL_PATTERN.test(combinedText)) return 'vehicle_fuel';
  if (VEHICLE_TRAVEL_PATTERN.test(combinedText)) return 'travel_expenses';
  if (AUTO_PARTS_PATTERN.test(combinedText)) return 'vehicle_repairs_licensing';
  if (VEHICLE_REPAIR_PATTERN.test(combinedText)) return 'vehicle_repairs_licensing';
  if (CLOTHING_VENDOR_PATTERN.test(vendorText) || CLOTHING_PATTERN.test(combinedText)) return 'uniform_costs';
  if (MEAL_VENDOR_PATTERN.test(vendorText) || MEAL_PATTERN.test(combinedText)) return 'meals';
  if (GROCERY_VENDOR_PATTERN.test(vendorText)) return 'supplies';
  if (HARDWARE_VENDOR_PATTERN.test(combinedText)) return 'repairs';
  if (BEAUTY_PATTERN.test(combinedText)) return 'supplies';

  if (absoluteAmount >= MIN_AUTO_VEHICLE_PURCHASE_AMOUNT && VEHICLE_PURCHASE_PATTERN.test(combinedText)) {
    return 'vehicle';
  }

  return normalizedCategory || 'supplies';
};

export default guardAutoDetectedReceiptExpenseCategory;
